import {
  Component,
  ElementRef,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from 'src/app/angular-services-v8/session.service';
import CustomerInfoAppLauncher from '@cemex-app/cmx-module-cc';
import { AppLauncher, Environment } from './types';
import { EnvironmentService, EnvironmentStructure } from '../../services';
import { SpaFunctions } from '../../helpers/functions';
import { ApplicationCode } from '../../spa';
import { WalkmeService } from 'src/app/services/walkme.service';

import { FeatureToggleService } from './../../feature-toggle-service/v1/src/feature-toggle.service';
import { AppFeatures } from '../../../toggleConfig';

import { IamAuthService } from 'src/app/msal-services-v1/services/iam-auth.service';

const PFX = '[CustomerInfoLauncher]';

@Component({
  selector: 'app-customer-info-launcher',
  templateUrl: './customer-info-launcher.component.html',
  styleUrls: ['./customer-info-launcher.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomerInfoLauncherComponent implements OnInit {
  private _selector: string;
  private s1JobsiteRequestsQuotes: boolean = false;
  private s1QuoteRequestsV2: boolean = false;
  private s1QuoteRequestsActions: boolean = false;
  private s1QuoteRequestsHistory: boolean = false;
  private s1QuoteRequestsPdf: boolean = false;
  private secret: string = "";
  private isLoginByIAM: boolean = false;


  constructor(
    elem: ElementRef,
    private activatedRoute: ActivatedRoute,
    private environmentService: EnvironmentService,
    private router: Router,
    private functions: SpaFunctions,
    public sessionService: SessionService,
    private walkmeService: WalkmeService,
    private featureToggleService: FeatureToggleService,
    private iamService: IamAuthService,
  ) {
    this.registerLauncher(new CustomerInfoAppLauncher());
    this._selector = elem.nativeElement.tagName.toLowerCase();
  }

  public ngOnInit() {
    // Prepare the DOM for mounting the application
    const rootNode = document.querySelector(this._selector);
    const mountElement = this.createMountElement();
    rootNode.appendChild(mountElement);
    // Get environment variables to bootstrap the application
    this.environmentService
      .getEnvironmentConfig('P1', EnvironmentStructure.JSON)
      .subscribe(
        (env) => {
          this.registerSessionService(this.sessionService);
          this.getFeatureToggleConfig();
          this.getIAMCredentials().then(() => {
            this.initReactApp(this.createEnvironment(env), mountElement);
          });
        },
        () =>
          console.log(
            PFX,
            'Init aborted. Could not set up environment configuration.'
          )
      );
    this.walkmeService.initializeService();
  }

  /**
   * @description
   *
   * Registers the application product launcher
   *
   */
  private registerLauncher(launcher: string): void {
    this.functions.registerGlobal(
      ApplicationCode.CUSTOMER_INFO,
      ['productLauncher'],
      launcher
    );
  }

  /**
   * @description
   *
   * Synchronizes history changes from inside the React application with Angular Router.
   *
   */
  private subscribeToHistory(): void {
    const history = this.functions.extractGlobal(
      ApplicationCode.CUSTOMER_INFO,
      ['browserHistory']
    );
    if (history) {
      history.listen(({ pathname, state }, action) => {
        if (
          !pathname.includes(this.getActivatedRoutePathname()) &&
          !(state && state.ngSimulated)
        ) {
          this.router.navigate([pathname]);
        }
      });
    } else {
      console.log(PFX, 'Could not attach listener to browser history.');
    }
  }

  /**
   * @description
   *
   * Initializes the React application from the registered product launcher.
   *
   */
  private initReactApp(
    environment: Environment,
    mountElement: HTMLElement
  ): void {
    this.functions
      .extractGlobal(ApplicationCode.CUSTOMER_INFO, ['productLauncher'])
      .init(mountElement, { environment }, () => {
        this.subscribeToHistory();
        console.log(PFX, 'App inited');
      });
  }

  /**
   * @description
   *
   * Registers the session service from which the React application will be listening
   * to legal entity changes.
   *
   */
  private registerSessionService(sessionService: any): void {
    this.functions.registerGlobal(
      ApplicationCode.USER_PROVISIONING,
      ['sessionService'],
      sessionService
    );
  }

  /**
   * @description
   *
   * Creates the environment object needed to bootstrap the React application.
   * @param env
   *
   */
  private createEnvironment(env): Environment {
    const environment: Environment = {
      apiHost: env['API_HOST'],
      appFeatures: 'api',
      basePath: '/commercialconditions',
      entryPath: this.getActivatedRoutePathname(),
      build: '',
      countlyKey: {
        CC: env['COUNTLY_KEY'] || '',
        CI: env['COUNTLY_KEY'] || '',
      },
      dcmHost: env['DCM_HOST'] || '',
      environment: env['ENV'] || 'quality',
      googleMapsApiKey: env['GOOGLE_MAPS_API_KEY'] || '',
      rolloutKey: env['ROLLOUT_APP_KEY'] || '',
      ibmClientId: env['CLIENT_ID'] || '',
      production: true,
      translations: {
        locale: env['LANGUAGE'],
        fallbackLocale: 'en_US',
        translateUrl: env['TRANSLATE_URL'],
      },
      cxAppName: env['CX_APP_NAME'],
      jobsiteRequestsQuotes: this.s1JobsiteRequestsQuotes,
      quoteRequestsV2: this.s1QuoteRequestsV2,
      quoteRequestsActions: this.s1QuoteRequestsActions,
      quoteRequestsHistory: this.s1QuoteRequestsHistory,
      quoteRequestsPdf: this.s1QuoteRequestsPdf,
      isLoginByIAM: this.isLoginByIAM,
      secret: this.secret,
    };
    return environment;
  }

  /**
   * @description
   *
   * Creates the mount element where the React application will render.
   *
   */
  private createMountElement(): HTMLElement {
    const element = document.createElement('div');
    element.id = 'react-app';
    return element;
  }

  /**
   * @description
   *
   * Retrieves the pathname from the activated route, which will be the entry point
   * for the React application.
   *
   */
  private getActivatedRoutePathname(): string {
    return `/${this.activatedRoute.snapshot.url.join('/')}`;
  }

  private getFeatureToggleConfig() {
    const country = this.sessionService.userCountry;
    this.s1JobsiteRequestsQuotes = this.featureToggleService.isEnabledFor(AppFeatures.S1JobsiteRequestsQuotes, { country });
    this.s1QuoteRequestsV2 = this.featureToggleService.isEnabledFor(AppFeatures.S1QuoteRequestsV2, { country });
    this.s1QuoteRequestsActions = this.featureToggleService.isEnabledFor(AppFeatures.S1QuoteRequestsActions, { country });
    this.s1QuoteRequestsHistory = this.featureToggleService.isEnabledFor(AppFeatures.S1QuoteRequestsHistory, { country });
    this.s1QuoteRequestsPdf = this.featureToggleService.isEnabledFor(AppFeatures.S1QuoteRequestsPdf, { country });
  }

  private async getIAMCredentials() {
    this.isLoginByIAM = this.iamService.isLoginByIAM();

    if (this.isLoginByIAM) {
      const signinAccount = this.iamService.getSigninAccount();
      this.secret = signinAccount?.idToken;

      if (!this.secret) {
        try {
          const token = await this.iamService.getAccessTokenDirect(['openid', 'profile']);
          this.secret = token || '';
        } catch (error) {
          console.error('Error getting direct access token:', error);
        }
      }

      if (!this.secret) {
        try {
          const token = await this.iamService.getTokenSilently();
          this.secret = token || '';
        } catch (error) {
          console.error('Error with base getTokenSilently:', error);
        }
      }

      if (!this.secret) {
        try {
          const msalTokenKey = Object.keys(sessionStorage).find(k => k.startsWith("msal.token.keys"));
          if (msalTokenKey) {
            const msalTokenKeysObj = JSON.parse(sessionStorage.getItem(msalTokenKey)!);
            const accessTokenKey = msalTokenKeysObj.accessToken?.[0];
            if (accessTokenKey) {
              const accessTokenObj = sessionStorage.getItem(accessTokenKey);
              if (accessTokenObj) {
                const accessTokenParsed = JSON.parse(accessTokenObj);
                this.secret = accessTokenParsed.secret || '';
              }
            }
          }
        } catch (error) {
          console.error('Error searching for secret in sessionStorage:', error);
        }
      }
    }
  }
}
